import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VIEW_PARAMS } from 'src/app/portfolio/views/global-nature-risk/siteExplorerView';
import { parseValidatedAtParam } from 'src/shared/utils/timePeriodHelpers';
import { isSiteFilterName } from 'src/app/portfolio/utils/sanitizeGnrFilters';
import { useAppDispatch, useAppSelector } from 'src/store';
import { SelectedFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import { SortingDirection } from 'src/shared/components/data-table/dataTableTypes';
import { setCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSlice';
import {
    updateGnrFilters,
    resetGnrFilters,
    syncGnrState,
    setSelectedAt as setSelectedAtAction,
    GnrFiltersState,
} from '../state/gnrFiltersSlice';
import { getGnrState } from '../state/gnrFiltersSelector';

const PAGE_PARAM = 'page';
const SEARCH_PARAM = 'search';
const SORT_PARAM = 'sort';
const SORT_DIR_PARAM = 'sortDir';
const CUSTOMER_ID_PARAM = 'customerId';
const AT_PARAM = 'at';

// The URL params kept through a clear. Not filters, and not site filter names.
const PRESERVED_NON_FILTER_PARAMS = [SEARCH_PARAM, SORT_PARAM, SORT_DIR_PARAM, CUSTOMER_ID_PARAM, AT_PARAM, ...VIEW_PARAMS];

type SortDirection = SortingDirection.ASC | SortingDirection.DESC;

const hasAnyGnrState = (state: GnrFiltersState): boolean =>
    state.selectedFilters.length > 0 ||
    (state.search ?? '') !== '' ||
    state.sort !== null ||
    (state.page ?? 1) > 1 ||
    state.selectedAt !== null;

const parseFiltersFromParams = (searchParams: URLSearchParams): SelectedFilters => {
    const filters: SelectedFilters = [];
    const seen = new Set<string>();
    searchParams.forEach((_, key) => {
        if (isSiteFilterName(key) && !seen.has(key)) {
            seen.add(key);
            filters.push({ name: key, values: searchParams.getAll(key) });
        }
    });
    return filters;
};

const useGnrFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const gnrState = useAppSelector(getGnrState);
    const { selectedFilters } = gnrState;

    // Kept current so the effect can read Redux state without it being a dependency.
    const gnrStateRef = useRef(gnrState);
    gnrStateRef.current = gnrState;

    // A GNR page is never project-scoped — clear a stale currentProjectId left over from a Nature
    // Performance site page, or it keeps project-scoped sidebar items pointing at that site.
    useEffect(() => {
        dispatch(setCurrentProjectId(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const hasUrlState = Array.from(searchParams.keys()).some(k => k !== CUSTOMER_ID_PARAM);

        if (hasUrlState) {
            // URL wins: sync all URL params into Redux (handles initial load, back/forward to parameterised URL).
            const sortDirParam = searchParams.get(SORT_DIR_PARAM);
            dispatch(
                syncGnrState({
                    selectedFilters: parseFiltersFromParams(searchParams),
                    search: searchParams.get(SEARCH_PARAM) ?? '',
                    sort: searchParams.get(SORT_PARAM),
                    sortDir: sortDirParam === SortingDirection.ASC || sortDirParam === SortingDirection.DESC ? sortDirParam : null,
                    page: parseInt(searchParams.get(PAGE_PARAM) ?? '', 10) || 1,
                    selectedAt: parseValidatedAtParam(searchParams, AT_PARAM),
                })
            );
        } else {
            // URL is empty — restore from Redux so in-app back-navigation preserves state.
            const redux = gnrStateRef.current;
            if (hasAnyGnrState(redux)) {
                const next = new URLSearchParams();
                const currentCustomerId = searchParams.get(CUSTOMER_ID_PARAM);
                if (currentCustomerId) next.set(CUSTOMER_ID_PARAM, currentCustomerId);
                redux.selectedFilters.forEach(({ name, values }) => values.forEach(v => next.append(name, v)));
                if (redux.search) next.set(SEARCH_PARAM, redux.search);
                if (redux.sort) next.set(SORT_PARAM, redux.sort);
                if (redux.sortDir) next.set(SORT_DIR_PARAM, redux.sortDir);
                if ((redux.page ?? 1) > 1) next.set(PAGE_PARAM, String(redux.page));
                if (redux.selectedAt) next.set(AT_PARAM, redux.selectedAt);
                setSearchParams(next, { replace: true });
            }
        }
    }, [searchParams, dispatch, setSearchParams]);

    const page = parseInt(searchParams.get(PAGE_PARAM) ?? '', 10) || 1;
    const search = searchParams.get(SEARCH_PARAM) ?? '';
    const sort = searchParams.get(SORT_PARAM);
    const sortDirParam = searchParams.get(SORT_DIR_PARAM);
    const sortDir: SortDirection | null =
        sortDirParam === SortingDirection.ASC || sortDirParam === SortingDirection.DESC ? sortDirParam : null;
    const selectedAt = parseValidatedAtParam(searchParams, AT_PARAM);

    const updateFilters = (name: string, values: string[]) => {
        const next = new URLSearchParams(searchParams);
        next.delete(name);
        values.forEach(v => next.append(name, v));
        next.set(PAGE_PARAM, '1');
        setSearchParams(next);

        const updatedFilters = selectedFilters.filter(f => f.name !== name).concat(values.length > 0 ? [{ name, values }] : []);
        dispatch(updateGnrFilters(updatedFilters));
    };

    const buildNonFilterParams = (): URLSearchParams => {
        const next = new URLSearchParams();
        PRESERVED_NON_FILTER_PARAMS.forEach(param => {
            const value = searchParams.get(param);
            if (value) {
                next.set(param, value);
            }
        });
        return next;
    };

    const resetFilters = () => {
        const next = buildNonFilterParams();
        next.set(PAGE_PARAM, '1');
        setSearchParams(next);
        dispatch(resetGnrFilters());
    };

    // Clears filters, the search and the time period in one URL update — resetFilters(),
    // setSearch('') and setSelectedAt(null) can't safely be called back-to-back, as each builds
    // its next params from the same pre-update searchParams snapshot and the last call's
    // setSearchParams wins, undoing the others.
    //
    // This backs the one Clear the reader is offered, on the bar that shows all three. The
    // search is preserved through `resetFilters` — the Clear inside the filter popover, which
    // carries no search box — and only there.
    const resetFiltersAndPeriod = () => {
        const next = buildNonFilterParams();
        next.delete(AT_PARAM);
        next.delete(SEARCH_PARAM);
        next.set(PAGE_PARAM, '1');
        setSearchParams(next);
        dispatch(resetGnrFilters());
        dispatch(setSelectedAtAction(null));
    };

    const applyFilters = (filters: SelectedFilters) => {
        const next = buildNonFilterParams();
        next.set(PAGE_PARAM, '1');
        filters.forEach(({ name, values }) => values.forEach(v => next.append(name, v)));
        setSearchParams(next);
        dispatch(updateGnrFilters(filters));
    };

    const setPage = (newPage: number) => {
        const next = new URLSearchParams(searchParams);
        next.set(PAGE_PARAM, String(newPage));
        setSearchParams(next);
    };

    const setSearch = (term: string) => {
        const next = new URLSearchParams(searchParams);
        if (term) {
            next.set(SEARCH_PARAM, term);
        } else {
            next.delete(SEARCH_PARAM);
        }
        next.set(PAGE_PARAM, '1');
        setSearchParams(next);
    };

    const setSort = (columnId: string | null, direction: SortDirection | null) => {
        const next = new URLSearchParams(searchParams);
        if (columnId && direction) {
            next.set(SORT_PARAM, columnId);
            next.set(SORT_DIR_PARAM, direction);
        } else {
            next.delete(SORT_PARAM);
            next.delete(SORT_DIR_PARAM);
        }
        next.set(PAGE_PARAM, '1');
        setSearchParams(next);
    };

    // Unlike every other GNR filter, this persists across all GNR views (portfolio and site level),
    // not just across GNR sub-routes — see gnrFiltersSlice.GnrFiltersState.selectedAt.
    const setSelectedAt = (at: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (at) {
            next.set(AT_PARAM, at);
        } else {
            next.delete(AT_PARAM);
        }
        next.set(PAGE_PARAM, '1');
        setSearchParams(next);
        dispatch(setSelectedAtAction(at));
    };

    return {
        selectedFilters,
        page,
        search,
        sort: sort ?? null,
        sortDir,
        selectedAt,
        updateFilters,
        resetFilters,
        resetFiltersAndPeriod,
        applyFilters,
        setPage,
        setSearch,
        setSort,
        setSelectedAt,
    };
};

export default useGnrFilters;
